/**
 * THE FACE SCALE — five faces, and the tap is the log.
 *
 * A coarse honest rating that costs one press and reads as a feeling rather than a digit. There is
 * no Save: a check-in that costs two presses gets skipped, and a weekly check-in that gets skipped
 * has no trend to draw. Selecting logs immediately; tapping another changes it.
 *
 * RNR's Radio Group is the semantic underneath — one value of five, `role="radiogroup"` on the set
 * and `role="radio"` with a checked state on each face — so a screen reader says "Great, 5 of 5,
 * selected" rather than reading five unrelated buttons. The FACE is the radio: there is no dot
 * beside a label, because the thing being chosen is the picture.
 *
 * The five tones are DATA and not decoration: rough means rough in both colour modes. They are
 * still THEME TOKENS — `--face-1` … `--face-5` and `--face-ink`, declared identically in light and
 * dark in the hunsy block of `_design.md` and absent from the Tuxedo table, so the second look
 * falls through to them. That is what makes "a colour comes from a token" hold here without the
 * scale following the room. Never partner colours and never `--success`/`--destructive` — a
 * feeling is not a status. Faces stay round whatever the theme's corner radius says.
 *
 * The tokens arrive as CLASSES rather than as strings, because the set is five and fixed: every
 * class name below is a literal in this file, which is the one form NativeWind can carry to
 * native. (The gels in `lib/partner.ts` are the opposite case — chosen per row off data — and
 * that is why they are literal hex and this is not.)
 */
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import * as React from 'react';
import { View } from 'react-native';

/**
 * The face drawn ON a tile. Dark, because every fill below is mid-tone — one ink for all five
 * rather than a contrast calculation per level.
 *
 * `--face-ink` is the OPAQUE near-black; the 0.82 is element opacity carried here rather than
 * alpha in the token, and the two are the same pixels for a solid shape on a solid fill. A token
 * has to be a colour a native View can take, and the generated roles are `hsl(var(--x))` with no
 * `<alpha-value>` slot, so `bg-face-ink/[0.82]` would be a gamble on how the build rewrites it.
 */
const INK = 'bg-face-ink';
const INK_EDGE = 'border-face-ink';
const INK_ALPHA = 0.82;

/**
 * `mouth` is the curve: -1 a deep frown, 0 a flat line, +1 a broad smile.
 *
 * `tone` and `edge` are the level's token as a fill class and as a border class — the same
 * colour, and two classes because a ring is drawn with one and a face with the other.
 */
export const FACES = [
  { level: 1, word: 'Rough', tone: 'bg-face-1', edge: 'border-face-1', mouth: -1 },
  { level: 2, word: 'Meh', tone: 'bg-face-2', edge: 'border-face-2', mouth: -0.5 },
  { level: 3, word: 'Okay', tone: 'bg-face-3', edge: 'border-face-3', mouth: 0 },
  { level: 4, word: 'Good', tone: 'bg-face-4', edge: 'border-face-4', mouth: 0.5 },
  { level: 5, word: 'Great', tone: 'bg-face-5', edge: 'border-face-5', mouth: 1 },
] as const;

export function faceFor(level: number) {
  return FACES.find((f) => f.level === level) ?? null;
}

/**
 * The smallest a face may be DRAWN. Not the smallest it may be TAPPED.
 *
 * Those were the same number while the row wrapped, and the wrap is what had to go: at 320 the
 * five faces broke 4+1, which drops "Great" onto its own line and destroys the one thing a scale
 * is — an ordinal row you read left to right. So the row never wraps and the face shrinks to fit
 * instead, down to 40px of drawn circle. The TARGET is still a finger's: `RadioGroupItem` carries
 * `min-h-11 min-w-11` and the cell is a column of face plus label, so the pressable box stays
 * comfortably over 44 in both directions however small the circle inside it gets.
 */
const FACE_MIN = 40;
/** Five faces plus four of these fill the row exactly; the chosen face's ring hangs in the gap. */
const GAP = 6;
/** The ring on the chosen face, and how far outside it sits. */
const RING = 2;
const RING_INSET = 5;

/**
 * One face, drawn rather than typed.
 *
 * No emoji, deliberately: a colour font ignores `color`, so the level's own colour — the thing the
 * scale is actually made of — would not survive the glyph. Two dots and one curved edge is the
 * whole face, and the curve is a box showing a single heavily-rounded border, which is the one arc
 * primitive available without a drawing library.
 */
export function Face({ level, size, dim }: { level: number; size: number; dim?: boolean }) {
  const face = faceFor(level);
  if (!face) return null;

  const eye = Math.max(2, Math.round(size * 0.095));
  const stroke = Math.max(1.5, Math.round(size * 0.05));
  const mouthW = Math.round(size * 0.42);
  const depth = Math.round(size * 0.15 * Math.abs(face.mouth));

  const mouth =
    face.mouth === 0 ? (
      <View className={INK} style={{ width: mouthW, height: stroke, borderRadius: stroke / 2, opacity: INK_ALPHA }} />
    ) : (
      <View
        className={INK_EDGE}
        style={{
          width: mouthW,
          height: depth + stroke,
          opacity: INK_ALPHA,
          ...(face.mouth > 0
            ? { borderBottomWidth: stroke, borderBottomLeftRadius: mouthW, borderBottomRightRadius: mouthW }
            : { borderTopWidth: stroke, borderTopLeftRadius: mouthW, borderTopRightRadius: mouthW }),
        }}
      />
    );

  const eyeStyle = { width: eye, height: eye, borderRadius: eye / 2, opacity: INK_ALPHA };

  return (
    <View
      className={face.tone}
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        alignItems: 'center',
        justifyContent: 'center',
        gap: Math.max(2, Math.round(size * 0.08)),
        // "about 40%" — the unchosen faces stay legible as a scale rather than disappearing.
        opacity: dim ? 0.38 : 1,
      }}>
      <View style={{ flexDirection: 'row', gap: Math.round(size * 0.2) }}>
        <View className={INK} style={eyeStyle} />
        <View className={INK} style={eyeStyle} />
      </View>
      {mouth}
    </View>
  );
}

export function FaceScale({
  value,
  onChange,
  size = 52,
  disabled,
  className,
  aside,
  children,
}: {
  /** The level in force, or null before anything has been logged. */
  value: number | null;
  /** Called the moment a face is tapped. This IS the log — there is no submit. */
  onChange: (level: number) => void;
  /** The face diameter the row would LIKE. It shrinks below this to fit, never above it. */
  size?: number;
  disabled?: boolean;
  className?: string;
  /**
   * One short fact drawn on the status line, at the other end of it — Pulse's weeks-logged run.
   * It rides here rather than under the whole block because it is about the row of faces: a streak
   * is the reason to press one, and a reason to press something belongs beside the thing.
   */
  aside?: React.ReactNode;
  /**
   * The optional note, folded beneath. It lives at the call site because the call site owns
   * saving it; the spec's rule is only that it is optional and never required.
   */
  children?: React.ReactNode;
}) {
  /**
   * THE ROW MEASURES ITSELF AND THE FACES FIT.
   *
   * A fixed diameter plus `flex-wrap` is the 4+1 bug: at 320 five 52px faces and their gaps want
   * 284px inside a card that has about 256, so the fifth one goes to the next line. Five `flex-1`
   * cells in a row that CANNOT wrap is the shape, and the circle drawn in each is the cell width
   * — floored at `FACE_MIN`, capped at what the caller asked for, so a wide card gets the board's
   * 60px faces and a narrow one gets 46px ones rather than a broken scale.
   *
   * Measured, not branched: this is the width of THIS row inside whatever column it was handed, so
   * it is the same answer beside a detail pane as it is on a phone. It is not a breakpoint and
   * `useLayout` is still the only place one may appear.
   */
  const [rowWidth, setRowWidth] = React.useState(0);
  const diameter =
    rowWidth > 0 ? Math.max(FACE_MIN, Math.min(size, Math.floor((rowWidth - GAP * 4) / 5))) : Math.max(FACE_MIN, size);
  const chosen = value === null ? null : faceFor(value);

  return (
    <View className={cn('gap-3', className)}>
      <RadioGroup
        value={value === null ? undefined : String(value)}
        onValueChange={(next) => onChange(Number(next))}
        disabled={disabled}
        accessibilityLabel="How this week was"
        className="w-full flex-row flex-nowrap pt-1"
        style={{ gap: GAP }}
        onLayout={(e) => setRowWidth(e.nativeEvent.layout.width)}>
        {FACES.map((f) => {
          const on = value === f.level;
          return (
            <RadioGroupItem
              key={f.level}
              value={String(f.level)}
              accessibilityLabel={`${f.word} — ${f.level} of 5`}
              disabled={disabled}
              className="min-w-0 flex-1 gap-1.5 active:opacity-70">
              <View style={{ width: diameter, height: diameter, alignItems: 'center', justifyContent: 'center' }}>
                {on ? (
                  /* The ring is in the level's OWN tone, not a theme accent: it belongs to the
                     face it is round, and a red ring round the "Great" face would read as an error.
                     It hangs outside the circle into the row's gap, which is why only one face may
                     ever wear one — and only one ever does. */
                  <View
                    className={f.edge}
                    style={{
                      position: 'absolute',
                      left: -RING_INSET,
                      top: -RING_INSET,
                      width: diameter + RING_INSET * 2,
                      height: diameter + RING_INSET * 2,
                      borderRadius: (diameter + RING_INSET * 2) / 2,
                      borderWidth: RING,
                    }}
                  />
                ) : null}
                <Face level={f.level} size={diameter} dim={value !== null && !on} />
              </View>
              <Text
                variant="small"
                numberOfLines={1}
                className={cn('text-center', on ? 'font-semibold' : 'text-muted-foreground')}>
                {f.word}
              </Text>
            </RadioGroupItem>
          );
        })}
      </RadioGroup>

      <View className="flex-row flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <Text variant="small" className="text-muted-foreground min-w-0 flex-1">
          {chosen === null ? 'Tap a face — it logs straight away.' : `Logged as ${chosen.word}. Tap another to change it.`}
        </Text>
        {aside}
      </View>

      {children}
    </View>
  );
}
